import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Search, Globe2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_TONE = {
  "Not Active":"bg-slate-100 text-slate-500 border-slate-200",
  "Low":"bg-slate-100 text-slate-700 border-slate-200",
  "Normal":"bg-sky-50 text-sky-700 border-sky-200",
  "Priority":"bg-emerald-50 text-emerald-800 border-emerald-200",
};
const COMPLIANCE_TONE = {
  "Unreviewed":"bg-slate-100 text-slate-500 border-slate-200",
  "Approved":"bg-emerald-50 text-emerald-700 border-emerald-200",
  "Restricted":"bg-amber-50 text-amber-800 border-amber-200",
  "Blocked":"bg-red-50 text-red-700 border-red-200",
};
const SERVICE_MODELS = ["StaffHub Direct","StaffHub Managed","StaffHub AI","StaffHub Hybrid","StaffHub Teams"];
const PRIORITIES = ["Not Active","Low","Normal","Priority"];
const COMPLIANCE = ["Unreviewed","Approved","Restricted","Blocked"];

export default function CountryAdmin() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("");
  const [priority, setPriority] = useState("");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/countries");
    setRows(data.countries);
  };
  useEffect(() => { load(); }, []);

  const regions = useMemo(() => [...new Set(rows.map(r => r.region))].sort(), [rows]);
  const filtered = useMemo(() => rows.filter(r => {
    if (q && !r.name.toLowerCase().includes(q.toLowerCase()) && !r.iso2.toLowerCase().includes(q.toLowerCase())) return false;
    if (region && r.region !== region) return false;
    if (priority && r.config.priority !== priority) return false;
    return true;
  }), [rows, q, region, priority]);

  const stats = useMemo(() => ({
    total: rows.length,
    active: rows.filter(r => r.config.active_recruiting).length,
    priority: rows.filter(r => r.config.priority === "Priority").length,
    approved: rows.filter(r => r.config.compliance_status === "Approved").length,
  }), [rows]);

  const save = async (iso2, patch) => {
    setBusy(true);
    try {
      await api.put(`/admin/countries/${iso2}`, patch);
      toast.success(`${iso2} updated`);
      await load();
      if (sel) setSel(prev => ({...prev, config: {...prev.config, ...patch}}));
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const toggleServiceModel = (sm) => {
    if (!sel) return;
    const cur = sel.config.supported_service_models || [];
    const next = cur.includes(sm) ? cur.filter(x => x !== sm) : [...cur, sm];
    save(sel.iso2, { supported_service_models: next });
  };

  return (
    <Layout>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Platform Settings · Geography</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Country Configuration</h1>
          <p className="text-sm text-slate-500 mt-1">Configure recruiting status, priority, service-model approval and compliance per country. Countries always remain available in the geography backend — this only controls how ConnectMyHub operates within each market.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatMini label="Countries in dataset" value={stats.total} tone="slate" testId="stat-total"/>
        <StatMini label="Active recruiting" value={stats.active} tone="emerald" testId="stat-active"/>
        <StatMini label="Priority markets" value={stats.priority} tone="sky" testId="stat-priority"/>
        <StatMini label="Compliance approved" value={stats.approved} tone="amber" testId="stat-approved"/>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input data-testid="country-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search country or ISO2…" className="w-full pl-9 pr-3 h-9 rounded-md border border-slate-200 text-sm bg-white outline-none focus:border-sky-500"/>
          </div>
          <select data-testid="filter-region" value={region} onChange={e=>setRegion(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">All regions</option>
            {regions.map(r => <option key={r}>{r}</option>)}
          </select>
          <select data-testid="filter-priority" value={priority} onChange={e=>setPriority(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">All priorities</option>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
          <div className="text-xs text-slate-500 ml-auto">{filtered.length} of {rows.length}</div>
        </div>
        <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100/70 text-slate-600 text-[11px] uppercase tracking-widest font-semibold sticky top-0">
              <tr>
                <th className="text-left px-4 py-3">Country</th>
                <th className="text-left px-4 py-3">Region</th>
                <th className="text-left px-4 py-3">Recruiting</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3">Service models</th>
                <th className="text-left px-4 py-3">Compliance</th>
                <th className="text-right px-4 py-3">Currency</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.iso2} data-testid={`country-row-${r.iso2}`} onClick={()=>setSel(r)}
                    className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded bg-slate-100 text-slate-500 flex items-center justify-center"><Globe2 size={12}/></div>
                      <div>
                        <div className="font-semibold text-slate-900">{r.name}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{r.iso2}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{r.region} <span className="text-[10px] text-slate-400">· {r.subregion}</span></td>
                  <td className="px-4 py-2.5">
                    {r.config.active_recruiting ? <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">Active</span>
                                                : <span className="text-[10px] font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-0.5">Not active</span>}
                  </td>
                  <td className="px-4 py-2.5"><span className={`text-[10px] font-semibold border rounded px-2 py-0.5 ${PRIORITY_TONE[r.config.priority]}`}>{r.config.priority}</span></td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(r.config.supported_service_models || []).length === 0 && <span className="text-[10px] text-slate-400">—</span>}
                      {(r.config.supported_service_models || []).slice(0,3).map(sm => <span key={sm} className="text-[9px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5">{sm.replace('StaffHub ','')}</span>)}
                      {(r.config.supported_service_models || []).length > 3 && <span className="text-[9px] text-slate-500">+{r.config.supported_service_models.length - 3}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5"><span className={`text-[10px] font-semibold border rounded px-2 py-0.5 ${COMPLIANCE_TONE[r.config.compliance_status]}`}>{r.config.compliance_status}</span></td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-700">{r.config.currency_override || r.currency}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-sm text-slate-500">No countries match your filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {sel && (
        <div className="fixed inset-0 z-40 bg-slate-900/50 flex justify-end" onClick={()=>setSel(null)}>
          <div onClick={e=>e.stopPropagation()} data-testid="country-drawer" className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-xl">
            <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h3 className="font-display font-bold text-xl text-slate-900">{sel.name}</h3>
                <p className="text-xs text-slate-500 font-mono">{sel.iso2} · {sel.region} · {sel.subregion} · {sel.currency}</p>
              </div>
              <button onClick={()=>setSel(null)} className="w-8 h-8 rounded hover:bg-slate-100 flex items-center justify-center"><X size={16}/></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Active recruiting</label>
                <div className="flex gap-2">
                  <button data-testid="drawer-active-on" onClick={()=>save(sel.iso2, {active_recruiting: true})}
                          className={`px-3 h-8 rounded border text-xs font-semibold ${sel.config.active_recruiting ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-slate-300 text-slate-600"}`}>Active</button>
                  <button data-testid="drawer-active-off" onClick={()=>save(sel.iso2, {active_recruiting: false})}
                          className={`px-3 h-8 rounded border text-xs font-semibold ${!sel.config.active_recruiting ? "bg-slate-100 border-slate-400 text-slate-800" : "bg-white border-slate-300 text-slate-600"}`}>Not active</button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Recruiting priority</label>
                <select data-testid="drawer-priority" value={sel.config.priority} onChange={e=>save(sel.iso2, {priority: e.target.value})}
                        className="w-full h-9 px-3 rounded border border-slate-300 text-sm">
                  {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Supported service models</label>
                <div className="grid grid-cols-2 gap-1.5">
                  {SERVICE_MODELS.map(sm => {
                    const on = (sel.config.supported_service_models || []).includes(sm);
                    return (
                      <button key={sm} data-testid={`sm-${sm.replace(' ','-')}`} onClick={()=>toggleServiceModel(sm)}
                              className={`text-left px-3 py-2 rounded border text-xs font-semibold ${on ? "bg-sky-50 border-sky-300 text-sky-800" : "bg-white border-slate-300 text-slate-600"}`}>
                        {on ? "✓ " : ""}{sm}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Compliance status</label>
                <select data-testid="drawer-compliance" value={sel.config.compliance_status} onChange={e=>save(sel.iso2, {compliance_status: e.target.value})}
                        className="w-full h-9 px-3 rounded border border-slate-300 text-sm">
                  {COMPLIANCE.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Currency override</label>
                <input data-testid="drawer-currency" defaultValue={sel.config.currency_override || ""} placeholder={sel.currency}
                       onBlur={e => e.target.value !== (sel.config.currency_override || "") && save(sel.iso2, {currency_override: e.target.value})}
                       className="w-full h-9 px-3 rounded border border-slate-300 text-sm"/>
                <div className="text-[10px] text-slate-500 mt-1">Leave empty to use dataset default ({sel.currency}).</div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Recruiting notes</label>
                <textarea data-testid="drawer-notes" defaultValue={sel.config.recruiting_notes || ""} rows={4}
                          onBlur={e => e.target.value !== (sel.config.recruiting_notes || "") && save(sel.iso2, {recruiting_notes: e.target.value})}
                          className="w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none"
                          placeholder="Sourcing channels, local partners, compliance considerations, comp benchmarks…"/>
              </div>

              {sel.config.updated_at && <div className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">Last updated {new Date(sel.config.updated_at).toLocaleString()}</div>}
              {busy && <div className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Saving…</div>}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const StatMini = ({label, value, tone, testId}) => (
  <div data-testid={testId} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
    <div className={`font-display text-2xl font-bold text-${tone}-900 tracking-tight`}>{value}</div>
  </div>
);
